import { SPLoading } from '@/components/commons';
import { IS_IOS_SAFARI } from '@/lib/common-react/utils/environment';
import theme from '@/styles/theme';
import { css } from '@emotion/react';
import useTranslation from 'next-translate/useTranslation';
import { CloseIconStoryGame } from 'public/assets';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface StoryGameImageProfileCropPopupProps {
  initialImageUrl?: string;
  onCrop: (croppedImage: File) => void;
  onBack: () => void;
}

const StoryGameImageProfileCropPopup: React.FC<
  StoryGameImageProfileCropPopupProps
> = ({ initialImageUrl, onCrop, onBack }) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const loadImage = useCallback(
    (url: string, withCrossOrigin = true) => {
      const img = new Image();

      if (withCrossOrigin) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        setImageUrl(url);
      };

      img.onerror = () => {
        if (withCrossOrigin) {
          loadImage(url, false);
        } else {
          console.error('Image failed to load');
          alert(
            t(
              'storygame_ai_onboarding_screen_description_error_unable_load_image',
            ),
          );
          onBack();
        }
      };
      img.src = url;
    },
    [onBack],
  );

  useEffect(() => {
    if (initialImageUrl) {
      loadImage(initialImageUrl);
    } else {
      alert(
        t('storygame_ai_onboarding_screen_description_error_generating_image'),
      );
      onBack();
    }
  }, [initialImageUrl, loadImage, onBack]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      const containerWidth = containerRef.current?.clientWidth || width;
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          1,
          containerWidth,
          (containerWidth / width) * height,
        ),
        containerWidth,
        (containerWidth / width) * height,
      );
      setCrop(crop);
    },
    [],
  );

  const cropImage = useCallback(async () => {
    if (completedCrop && imgRef.current && canvasRef.current) {
      const image = imgRef.current;
      const canvas = canvasRef.current;
      const crop = completedCrop;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext('2d');

      const pixelRatio = window.devicePixelRatio;

      canvas.width = Math.floor(crop.width * pixelRatio);
      canvas.height = Math.floor(crop.height * pixelRatio);

      if (ctx) {
        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        ctx.drawImage(
          image,
          cropX,
          cropY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        );

        try {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                console.error('Canvas is empty');
                return;
              }
              const croppedFile = new File([blob], 'cropped-image.png', {
                type: 'image/png',
              });
              onCrop(croppedFile);
              onBack();
            },
            'image/png',
            1,
          );
        } catch (error) {
          console.error('Failed to create blob:', error);
          alert(
            t(
              'storygame_ai_onboarding_screen_description_error_processing_image',
            ),
          );
        }
      }
    }
  }, [completedCrop, onCrop, onBack]);

  if (!imageUrl) {
    return null;
  }

  return (
    <div css={styles} ref={containerRef}>
      <div className="profile-crop-container">
        <div className="crop-header">
          <div className="left">
            <div>
              {t('storygame_ai_onboarding_popup_title_crop_profile_image')}
            </div>
          </div>
          <button className="close-button" onClick={onBack}>
            <CloseIconStoryGame />
          </button>
        </div>
        <div className="image-preview">
          {imageUrl && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              circularCrop
            >
              <img
                ref={imgRef}
                src={imageUrl}
                onLoad={onImageLoad}
                alt="Crop me"
                crossOrigin="anonymous"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            </ReactCrop>
          )}
          {!imageUrl && <SPLoading isFixed size="6rem" />}
        </div>
        <div className="button-container">
          <button className="crop-button" onClick={cropImage}>
            {t('storygame_ai_onboarding_popup_button_crop')}
          </button>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

const styles = css`
  @media (max-width: ${theme.devices.md}px) {
    height: 100vh;
    width: 100vw;
    max-width: 100%;
    ${IS_IOS_SAFARI ? 'max-height: -webkit-fill-available' : ''};

    // @supports (100dvh) {
    //   height: 100dvh;
    // }
    // @supports not (height: 100dvh) {
    //   height: 95vh;
    // }
    height: calc(var(--inner_vh, 1vh) * 100);
  }

  .profile-crop-container {
    width: 100%;
    max-width: 420px;
    background: rgba(0, 0, 0, 0.75);
    border-radius: 10px;
    padding: 20px;
    color: white;
    backdrop-filter: blur(12px);
    justify-content: space-between;
    display: flex;
    flex-direction: column;

    .crop-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      .left {
        display: flex;
        align-items: center;
      }
    }

    @media (max-width: ${theme.devices.md}px) {
      width: 100%;
      height: 100%;
      border-radius: 0;
      max-width: none;
    }
  }

  .back-button {
    transform: rotate(180deg);
  }

  .image-preview {
    overflow: hidden;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    max-height: calc(100vh - 200px);

    @media (max-width: ${theme.devices.md}px) {
      max-height: calc(100vh - 150px);
    }
  }

  .image-preview img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .button-container {
    display: flex;
    width: 100%;
    margin-top: 20px;
    justify-content: center;
  }

  .crop-button {
    background: #8b5cf6;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    align-self: center;
    width: 50%;
    margin-bottom: env(safe-area-inset-bottom);
  }

  .close-button {
    background: none;
    border: none;
    cursor: pointer;
  }
`;

export default StoryGameImageProfileCropPopup;
